import { useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContentText,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import TagIcon from "@mui/icons-material/Tag";
import MoreIcon from "@mui/icons-material/More";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewListIcon from "@mui/icons-material/ViewList";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import ReleasesTable from "./ReleasesTable";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import LinearIndeterminate from "components/utils/LinearLoader";
import RequestWithSchedule from "components/utils/RequestWithSchedule";
import { ProjectConfigSchema } from "hooks/pavm/base";
import { usePavmRequestFwupdate } from "hooks/pavm/request";
import {
  usePavmFwupdateRemove,
  usePavmFwupdateStatus,
} from "hooks/pavm/fwupdate";

type ReleasesViewProps = {
  type: string;
  title: string;
  config: ProjectConfigSchema;
  open: boolean;
  onClose: any;
};

const ms_to_utc = (ms: number) => {
  const ms_utc = new Date(ms);
  const ms_utc_offset = ms_utc.getTimezoneOffset();
  const ms_exact = new Date(ms - ms_utc_offset * 60 * 1000); // offset ms => minutes
  return ms_exact.toUTCString();
};

const ReleasesView = (props: ReleasesViewProps) => {
  const { onClose, open, config } = props;
  const { host, ip, version } = config;

  const remove = usePavmFwupdateRemove(host);
  const status = usePavmFwupdateStatus(host);
  const fwupdate = usePavmRequestFwupdate();

  const uploaded = status.data?.uploaded;
  const installed = status.data?.installed;

  const handleClose = () => onClose();
  const handleRemove = () => remove.mutate();

  const requestInstall = (triggerAt: number, reason: string) =>
    fwupdate.mutate({
      ip,
      host,
      reason,
      fwupdate: {
        size: uploaded?.size ?? 0,
        name: uploaded?.name ?? "",
        hash: uploaded?.hash ?? "",
        triggerAt,
        lastModified: uploaded?.lastModified ?? "",
      },
    });

  // refetch fwupdate status if request successful
  useEffect(() => {
    if (fwupdate.isSuccess) status.refetch();
  }, [fwupdate.isSuccess]);

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
      <Box sx={conf.STYLE.DIALOG}>
        <Container maxWidth="xl">
          {/* INSTALLED */}
          <SectionHeader
            title="Installed"
            icon={<ViewListIcon />}
            buttons={[
              <Chip
                key={"releases-host"}
                color="primary"
                label={config.host}
                sx={{ ml: 1 }}
              />,
              <Button
                key={"releases-exit"}
                color="inherit"
                variant="outlined"
                onClick={handleClose}
                sx={{ ml: 2 }}
              >
                <CloseIcon />
              </Button>,
            ]}
          />

          <TableContainer sx={{ boxShadow: 2, mb: 2 }}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell />

                  <TableCell align="right">
                    <b>Version</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Size (MB)</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Tag</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Algo</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Installed</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {status.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <LinearIndeterminate />
                    </TableCell>
                  </TableRow>
                ) : !installed ? (
                  <EmptyTableRow colSpan={8} type="release" />
                ) : (
                  <TableRow
                    key={installed?.name}
                    sx={conf.LAST_CHILD_NO_BORDER}
                  >
                    <TableCell>
                      <b>{installed?.name?.replace("releases/", "") ?? "--"}</b>
                    </TableCell>

                    <TableCell align="right">{version}</TableCell>

                    <TableCell align="right">
                      {installed?.size
                        ? Math.round(installed?.size / 10000) / 100
                        : "--"}
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title={installed?.eTag ?? "--"}>
                        <TagIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title={installed?.checksumAlgorithm ?? "--"}>
                        <MoreIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      {installed?.triggerAt
                        ? ms_to_utc(installed.triggerAt)
                        : "--"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* UPLOADED */}
          <SectionHeader
            title="Uploaded"
            icon={<ViewListIcon />}
            buttons={[
              <Refresh
                onClick={status.refetch}
                isFetching={status.isLoading}
              />,
            ]}
          />

          <TableContainer sx={{ boxShadow: 2, mb: 2 }}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell />

                  <TableCell align="right">
                    <b>Status</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Size (MB)</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Tag</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Algo</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Install at</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>Install</b>
                  </TableCell>

                  <TableCell align="right">
                    <DeleteIcon fontSize="small" />
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {status.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearIndeterminate />
                    </TableCell>
                  </TableRow>
                ) : uploaded?.status === "EMPTY" || !uploaded ? (
                  <EmptyTableRow colSpan={8} type="release" />
                ) : (
                  <TableRow key={uploaded?.hash} sx={conf.LAST_CHILD_NO_BORDER}>
                    <TableCell>
                      <b>{uploaded?.name?.replace("releases/", "") ?? "--"}</b>
                    </TableCell>

                    <TableCell align="right">{uploaded?.status}</TableCell>

                    <TableCell align="right">
                      {uploaded?.size
                        ? Math.round(uploaded?.size / 10000) / 100
                        : "--"}
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title={uploaded?.eTag ?? "--"}>
                        <TagIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title={uploaded?.checksumAlgorithm ?? "--"}>
                        <MoreIcon fontSize="small" />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">
                      {uploaded?.triggerAt
                        ? ms_to_utc(uploaded.triggerAt)
                        : "--"}
                    </TableCell>

                    <TableCell align="right">
                      <RequestWithSchedule
                        size="small"
                        type="Fwupdate Install"
                        details={
                          <>
                            <DialogContentText>
                              Tag: {uploaded?.eTag}
                            </DialogContentText>

                            <Grid item xs={12}>
                              <DialogContentText>
                                Size:{" "}
                                {uploaded?.size
                                  ? Math.round(uploaded?.size / 10000) / 100
                                  : "--"}{" "}
                                MB
                              </DialogContentText>
                            </Grid>

                            <DialogContentText>
                              Name: {uploaded?.name}
                            </DialogContentText>
                          </>
                        }
                        onSubmit={requestInstall}
                        onSuccess={fwupdate.isSuccess}
                        disabled={uploaded?.status !== "READY"}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={
                          uploaded?.status === "REQUESTED" ||
                          uploaded?.status === "SCHEDULED" ||
                          uploaded?.status === "INSTALLING"
                        }
                        onClick={handleRemove}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* RELEASES */}
          <ReleasesTable host={host} uploaded={uploaded} />
        </Container>
      </Box>
    </Dialog>
  );
};

export default ReleasesView;
